/**
 * 预渲染页面混入
 * 为每个页面提供预渲染相关的功能
 */
/**
 * 预渲染页面混入
 * 为页面提供预渲染 + SEO 协同能力
 */

import {
  getDetailListPath,
  isValidDetailId,
  normalizeRoutePath,
  resolveDetailParams,
} from '@/utils/detailRoute'

export default {
  data () {
    return {
      isPrerendering: false,
      pageLoaded: false,
      dataLoaded: false,
      // 详情页三态：loading / ready / error（notFound 仅在明确 HTTP 404 时可达）
      detailState: 'loading',
      detailErrorKind: '',
    }
  },

  created () {
    this.isPrerendering = this.$prerender.isPrerendering()
    // 请求簿记不放进 data：避免 Vue 2 递归劫持与无谓渲染
    this._detailReq = { token: 0, id: '', type: '' }
    this._detailDestroyed = false
  },

  beforeDestroy () {
    // 卸载后到达的响应不得再写数据 / SEO
    this._detailDestroyed = true
    this.invalidateDetailRequest()
  },

  mounted () {
    this.pageLoaded = true
    this.initPageSEO()

    this.$prerender.clientOnly(() => {
      this.onClientMounted()
    })
  },

  computed: {
    /** 详情页对应列表入口：空 id / 非法 id 的兜底去处，也是错误态的“回列表” */
    detailListPath () {
      const { basePath, type } = resolveDetailParams(this.$route || {})
      return getDetailListPath(basePath, type)
    },
    detailStateTitle () {
      if (this.detailState === 'error') return 'Unable to load this property right now.'
      if (this.detailState === 'notFound') return 'This property is no longer available.'
      return 'Loading property details'
    },
    detailStateMessage () {
      if (this.detailState === 'error') {
        return 'This may be a temporary issue. Please try again in a moment.'
      }
      if (this.detailState === 'notFound') {
        return 'The listing may have been removed. Please browse other properties.'
      }
      return 'Please wait a moment.'
    },
  },

  methods: {
    onClientMounted () {
      // 子组件可重写
    },

    async safeApiCall (apiCall, fallbackData = null) {
      return await this.$prerender.skipInPrerender(apiCall, fallbackData)
    },

    /** 请求身份（id + 业务 type）统一走 detailRoute 单一解析源 */
    resolveDetailRequestContext () {
      const params = resolveDetailParams(this.$route || {})
      const type = typeof this.getDetailRequestType === 'function'
        ? String(this.getDetailRequestType() || '')
        : params.type
      return { id: params.id, type, basePath: params.basePath }
    },

    /** 页面可重写：销售页返回归一化后的 house type */
    getDetailRequestType () {
      return this.getDetailPageType()
    },

    /** 让在途请求失效（数值自增，避免同 id 的 ABA 复用） */
    invalidateDetailRequest () {
      if (!this._detailReq) this._detailReq = { token: 0, id: '', type: '' }
      this._detailReq.token += 1
    },

    /**
     * 开始一次详情请求：递增 token 并做空 id / 非法 id 兜底
     * @returns {{ token: number, valid: boolean, id: string, type: string, basePath: string }}
     */
    beginDetailRequest () {
      this.invalidateDetailRequest()
      const context = this.resolveDetailRequestContext()
      const token = this._detailReq.token
      if (!isValidDetailId(context.id)) {
        this._detailReq.id = ''
        this._detailReq.type = ''
        return { token, valid: false, ...context }
      }
      this._detailReq.id = context.id
      this._detailReq.type = context.type
      return { token, valid: true, ...context }
    },

    /** 旧请求：token 不匹配、已卸载，或路由 id/type 已变 */
    isDetailRequestStale (token) {
      if (!this._detailReq || token !== this._detailReq.token) return true
      if (this._detailDestroyed === true) return true
      const context = this.resolveDetailRequestContext()
      if (context.id !== this._detailReq.id) return true
      return String(context.type) !== String(this._detailReq.type)
    },

    /**
     * 空 id / 非法 id 前端兜底：只跳这三个入口，不改路由令牌语义
     * 跳转不成立（无目标 / 无 router / 同路径）或 replace 被拒时转错误态，
     * 避免页面永久停在 loading，也不留下未处理的 Promise rejection。
     * @returns {boolean} 是否已发起跳转
     */
    redirectToDetailList (basePath, type) {
      const target = getDetailListPath(basePath, type)
      const currentPath = normalizeRoutePath((this.$route && this.$route.path) || '/')
      const canReplace =
        Boolean(target) &&
        currentPath !== normalizeRoutePath(target) &&
        Boolean(this.$router) &&
        typeof this.$router.replace === 'function'

      const markFailure = () => this.applyDetailFailure({ detailKind: 'incomplete' })

      if (!canReplace) {
        markFailure()
        return false
      }

      try {
        const result = this.$router.replace(target)
        // vue-router 3 在导航被中断 / 重复导航时会 reject
        if (result && typeof result.catch === 'function') {
          result.catch(markFailure)
        }
      } catch (error) {
        markFailure()
        return false
      }
      return true
    },

    /**
     * 纯取数：不在此处写任何 SEO。
     * 这里在 await 之后拿不到请求身份，也就无法判断新旧；若在此写 SEO，
     * 先返回的旧请求会先改掉新路由的 title / canonical / 结构化数据。
     * 成功页面必须在 token 校验通过、info 提交之后再写 SEO。
     */
    async smartDetailFetch (apiCall, pageType = 'letting', options = {}) {
      const { id } = resolveDetailParams(this.$route || {})
      return await this.$prerender.smartDataFetch(apiCall, pageType, id, options)
    },

    isDetailFallbackData (data) {
      return this.$prerender.isDetailFallbackData(data)
    },

    /**
     * 详情接口响应体 → 房源数据（唯一解析入口）
     * - 业务码：后端契约 `code === 1` 为成功；缺失时不做判断（兼容旧响应）
     * - payload：只接受非空 plain object（null / 数组 / {} 都视为没拿到房源）
     * - id：响应明确带 id 时必须与请求 id 一致（不额外推断 type 契约）
     * @param {{ data?: any }} [response]
     * @param {string|number} [expectedId] 本次请求的房源 id
     */
    readDetailResponse (response, expectedId) {
      const body = response && response.data
      const failure = (message) => {
        const error = new Error(message)
        error.detailKind = 'incomplete'
        return error
      }
      if (!body || typeof body !== 'object' || Array.isArray(body)) {
        throw failure('Empty response')
      }
      if (body.code !== undefined && body.code !== null && Number(body.code) !== 1) {
        throw failure(`Business failure: ${body.code}`)
      }

      const data = body.data
      if (
        !data ||
        typeof data !== 'object' ||
        Array.isArray(data) ||
        Object.keys(data).length === 0
      ) {
        throw failure('No data received')
      }

      // 响应里明确带了 id 却与请求 id 不同：这是串房，不能提交数据 / SEO
      const responseId = data.id
      const hasResponseId = responseId !== undefined && responseId !== null && responseId !== ''
      const hasExpectedId = expectedId !== undefined && expectedId !== null && expectedId !== ''
      if (hasResponseId && hasExpectedId && String(responseId) !== String(expectedId)) {
        throw failure(`Response id mismatch: ${responseId} !== ${expectedId}`)
      }

      return data
    },

    /** 失败态 SEO：noindex，且不输出房源结构化数据（不谎称房源存在/有 0 元价格） */
    applyDetailErrorSEO (kind) {
      if (!this.$prerender || typeof this.$prerender.applySeoPayload !== 'function') return
      const notFound = kind === 'notFound'
      this.$prerender.applySeoPayload({
        title: notFound
          ? 'Property not available | Yours Link'
          : 'Property details temporarily unavailable | Yours Link',
        description: notFound
          ? 'This property is no longer available on Yours Link.'
          : 'This property could not be loaded right now. Please try again later.',
        keywords: 'property, Yours Link',
        type: 'website',
        noindex: true,
      }, { route: this.$route, data: {} })
    },

    /** 统一失败落地：清空伪造字段 → 错误态 → noindex → 通知预渲染 fallback */
    applyDetailFailure (failure) {
      const kind = (failure && failure.detailKind) || 'unknown'
      this.detailErrorKind = kind
      // 只有明确 HTTP 404 才是 notFound；其余一律“暂时无法加载”
      this.detailState = kind === 'notFound' ? 'notFound' : 'error'
      this.dataLoaded = false
      this.info = this.$prerender.createEmptyDetailData()
      this.likeHome = []
      this.applyDetailErrorSEO(kind)
      this.notifyRenderComplete(this.info)
    },

    /** 手动重试（本轮不引入自动重试定时器） */
    retryDetail () {
      if (this.detailState === 'loading') return
      this.getDetail()
    },

    getDetailPageType () {
      const path = (this.$route && this.$route.path) || ''
      if (path.includes('letting')) return 'letting'
      if (path.includes('house')) return 'house'
      if (path.includes('management')) return 'management'
      return 'letting'
    },

    setSEO (seoData) {
      if (!seoData) return
      this.$prerender.applySeoPayload(seoData, {
        route: this.$route,
        data: this.info || {},
      })
    },

    getPageName () {
      return (this.$route && this.$route.name) || 'Home'
    },

    initPageSEO () {
      // 详情页的取数是异步的，且可能早于 mounted 完成：
      // 数据已落地或已进入 error/notFound 态时，不得再用通用路由 SEO 覆盖
      // 真实标题 / description / canonical / 结构化数据。
      if (
        this.$prerender.isDetailPage() &&
        (this.detailState !== 'loading' || this.dataLoaded)
      ) {
        return
      }
      const pageName = this.getPageName()
      const defaultSEO = this.$prerender.getDefaultSEO(pageName)
      this.setSEO(defaultSEO)
      this.$prerender.ensureRouteSEO(this.$route)
    },

    notifyRenderComplete (data = null) {
      if (!this.isPrerendering) return
      this.$nextTick(() => {
        const payload = data || this.info
        this.$prerender.triggerRenderEvent({
          fallback: !this.$prerender.isDataReadyForPrerender(payload),
        })
      })
    },
  }
}
